import React, { createContext, useContext, useEffect, useState } from "react";
import {
  useInView,
  type IntersectionOptions,
} from "react-intersection-observer";

type TrackerNavigationContextData = {
  onItemVisible: (id: string, isVisible: boolean) => void;
  /** List of ids that are currently in the viewport */
  visibleItems: Set<string>;
};

export const TrackerNavigationContext = createContext<
  TrackerNavigationContextData | undefined
>(undefined);

export interface TrackerNavigationProviderProps {
  children: React.ReactNode;
}

export const TrackerNavigationProvider: React.FC<
  TrackerNavigationProviderProps
> = ({ children }) => {
  const [visibleItems, setVisibleItems] = useState<Set<string>>(new Set());

  const onItemVisible = (visibleId: string, visible: boolean) => {
    if (visible) {
      visibleItems.add(visibleId);
    } else {
      visibleItems.delete(visibleId);
    }

    setVisibleItems(new Set(visibleItems));
  };

  return (
    <TrackerNavigationContext.Provider
      value={{
        onItemVisible,
        visibleItems,
      }}
    >
      {children}
    </TrackerNavigationContext.Provider>
  );
};

export function useVisibleTrackerNavItems() {
  const contextData = useContext(TrackerNavigationContext);
  if (contextData === undefined) {
    throw new Error(
      "useVisibleTrackerNavItems must be used within the TrackerNavigationProvider"
    );
  }

  return contextData.visibleItems;
}

/**
 * Return a ref which will trigger a hook update when the element using the ref comes into view.
 *
 * This uses [react-intersection-observer](https://www.npmjs.com/package/react-intersection-observer),
 * which leverages the browser's native [Intersection Observer API](https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API)
 */
export function useTrackerNavigationItemRef(
  id: string,
  options?: IntersectionOptions
) {
  const contextData = useContext(TrackerNavigationContext);
  if (contextData === undefined) {
    throw new Error(
      "useTrackerNavigationItemRef must be used within the TrackerNavigationProvider"
    );
  }

  const { ref, inView } = useInView(options);

  const { onItemVisible } = contextData;

  useEffect(() => {
    onItemVisible(id, inView);

    // onItemVisible reference will cause this to run continuously, so do not add it
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, inView]);

  return ref;
}
